import { Button, Checkbox, Group, Stack, TextInput } from '@mantine/core';
import {
    ESTABLISHMENT_WEEKDAYS,
    EstablishmentHours,
    EstablishmentWeekday,
    normalizeEstablishmentHours,
} from '@routellers/types/dist/city-establishment/establishment-hours';
import { useTranslation } from 'react-i18next';

export type HoursDayForm = { closed: boolean; open: string; close: string };

export const emptyHours = (): Record<EstablishmentWeekday, HoursDayForm> => ({
    mon: { closed: false, open: '', close: '' },
    tue: { closed: false, open: '', close: '' },
    wed: { closed: false, open: '', close: '' },
    thu: { closed: false, open: '', close: '' },
    fri: { closed: false, open: '', close: '' },
    sat: { closed: false, open: '', close: '' },
    sun: { closed: false, open: '', close: '' },
});

export const hoursFrom = (
    hours: EstablishmentHours | null | undefined,
): Record<EstablishmentWeekday, HoursDayForm> => {
    const form = emptyHours();

    if (!hours) return form;

    for (const day of ESTABLISHMENT_WEEKDAYS) {
        const row = hours[day];

        if (!row) continue;
        if (row.closed) form[day] = { closed: true, open: '', close: '' };
        else form[day] = { closed: false, open: row.open || '', close: row.close || '' };
    }

    return form;
};

const hhmm = (value: string) => value.trim().slice(0, 5);

export const hoursPayload = (
    hours: Record<EstablishmentWeekday, HoursDayForm>,
): EstablishmentHours | null => {
    const raw: EstablishmentHours = {};

    for (const day of ESTABLISHMENT_WEEKDAYS) {
        const row = hours[day];

        if (row.closed) raw[day] = { closed: true };
        else if (row.open.trim() && row.close.trim()) {
            raw[day] = { open: hhmm(row.open), close: hhmm(row.close) };
        }
    }

    return normalizeEstablishmentHours(raw);
};

/** A day with only one of open/close filled. Missing days stay unknown. */
export const unpairedHourDay = (
    hours: Record<EstablishmentWeekday, HoursDayForm>,
): EstablishmentWeekday | null => {
    for (const day of ESTABLISHMENT_WEEKDAYS) {
        const row = hours[day];

        if (row.closed) continue;
        if (Boolean(row.open.trim()) !== Boolean(row.close.trim())) return day;
    }

    return null;
};

export const WeekHoursFields = ({
    hours,
    onChange,
}: {
    hours: Record<EstablishmentWeekday, HoursDayForm>;
    onChange: (next: Record<EstablishmentWeekday, HoursDayForm>) => void;
}) => {
    const { t } = useTranslation();

    const patch = (day: EstablishmentWeekday, next: HoursDayForm) => {
        onChange({ ...hours, [day]: next });
    };

    const copyWeekdays = () => {
        const monday = hours.mon;
        const next = { ...hours };

        for (const day of ['tue', 'wed', 'thu', 'fri'] as const) next[day] = { ...monday };
        onChange(next);
    };

    return (
        <Stack gap='xs'>
            <Button variant='light' size='xs' w='fit-content' onClick={copyWeekdays}>
                {t('pages.establishments.editor.copyWeekdays')}
            </Button>
            {ESTABLISHMENT_WEEKDAYS.map((day) => {
                const row = hours[day];

                return (
                    <Group key={day} grow align='flex-end'>
                        <Checkbox
                            label={t(`enums.weekday.${day}`)}
                            checked={row.closed}
                            onChange={(event) =>
                                patch(day, {
                                    closed: event.currentTarget.checked,
                                    open: '',
                                    close: '',
                                })
                            }
                            description={t('pages.establishments.editor.closedDay')}
                        />
                        <TextInput
                            label={t('pages.establishments.editor.opens')}
                            value={row.open}
                            disabled={row.closed}
                            placeholder='09:00'
                            onChange={(event) =>
                                patch(day, { ...row, open: event.currentTarget.value })
                            }
                        />
                        <TextInput
                            label={t('pages.establishments.editor.closes')}
                            value={row.close}
                            disabled={row.closed}
                            placeholder='18:00'
                            onChange={(event) =>
                                patch(day, { ...row, close: event.currentTarget.value })
                            }
                        />
                    </Group>
                );
            })}
        </Stack>
    );
};
